import cx from 'classnames';
import * as React from 'react';

import { DatasheetGetDto } from '@common/dto/datasheet/get-dto';
import { IDatasheetComponentDto } from '@common/dto/datasheet/datasheet-component';
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from '@modules/datasheets/types/datasheet-element';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { DatasheetNavigationContext, useDatasheetNavigation, FieldValidationContext, FieldValidationContextType, DatasheetForceDisabledContext } from './context';

import { TextField } from './components/text-field';
import { TextElement } from './components/text-element';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';
import { FieldType } from '@common/dto/field/type';
import { DateField } from './components/date-field';
import { NumberField } from './components/number-field';
import { DropdownField } from './components/dropdown-field';
import { HyperlinkField } from './components/hyperlink-field';
import { ImageField } from './components/image-field';
import { LookupTableField } from './components/lookup-table-field';
import { TableField } from './components/table-field';

import './datasheet.styles.scss';
import { CheckboxField } from './components/checkbox-field';
import { isTabNavigationTarget } from '@modules/datasheets/utils/tab-navigation';

export type DatasheetProps = {
  gridSettingsMargin: {
    show: boolean;
    width: number;
    height: number;
  };
  pageBreaks: {
    show: boolean;
    width: number;
    height: number;
  };
  datasheet: DatasheetGetDto;
  datasheetElements: Array<DatasheetElement>;
  className?: string;
  printableDatasheetHolderId: string;
  onDatasheetElementUpdate: <T extends IDatasheetComponentDto = IDatasheetComponentDto>(
    id: DatasheetElementId,
    data: DatasheetElement<T>
  ) => void;
  context?: number;
  globalId?: number;
  /** Parent datasheet format id (for opening table datasheet result in new tab). */
  parentFormatId?: number;
  /** Parent datasheet current tab id (for opening table datasheet result in new tab). */
  parentTabId?: number;
  /** When true, datasheet is in pedigree split view; refresh subtext/symbols after updateValue */
  formatOnly?: boolean;
  fieldValidation?: FieldValidationContextType;
  splitViewDisabled?: boolean;
};

export const Datasheet: React.FC<DatasheetProps> = (props) => {
  const { datasheetElements, onDatasheetElementUpdate, className, printableDatasheetHolderId, context, globalId, parentFormatId, parentTabId, formatOnly, fieldValidation, splitViewDisabled } = props;

  const forceDisabled = splitViewDisabled === true || (formatOnly === true && (!globalId || globalId === 0));

  const tabOrderedIds = React.useMemo(
    () => {
      const asField = (el: (typeof datasheetElements)[number]) =>
        el as DatasheetElement<DatasheetFieldComponentGetDto>;

      return datasheetElements
        .filter((el) => {
          if (el.type !== DatasheetElementType.field) return false;
          return isTabNavigationTarget(asField(el).original);
        })
        .sort((a, b) => {
          const orderA = asField(a).original.tabOrder;
          const orderB = asField(b).original.tabOrder;

          if (orderA > 0 && orderB > 0) return orderA - orderB;

          if (orderA > 0) return -1;
          if (orderB > 0) return 1;

          const yDiff = a.coordinates.y - b.coordinates.y;
          return yDiff !== 0 ? yDiff : a.coordinates.x - b.coordinates.x;
        })
        .map((el) => el.id);
    },
    [datasheetElements],
  );

  const navigationValue = useDatasheetNavigation(tabOrderedIds);

  const defaultFieldValidation = React.useMemo<FieldValidationContextType>(() => ({
    fieldErrors: {},
    setFieldError: () => {},
    clearFieldError: () => {},
  }), []);

  const forceDisabledContentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = forceDisabledContentRef.current;
    if (!el) {
      return;
    }
    if (forceDisabled) {
      el.setAttribute('inert', '');
      const active = document.activeElement;
      if (active instanceof HTMLElement && el.contains(active)) {
        active.blur();
      }
    } else {
      el.removeAttribute('inert');
    }
  }, [forceDisabled]);

  return (
    <DatasheetForceDisabledContext.Provider value={forceDisabled}>
    <FieldValidationContext.Provider value={fieldValidation ?? defaultFieldValidation}>
    <DatasheetNavigationContext.Provider value={navigationValue}>
    <div className={cx("DatasheetsResultDatasheet", className, forceDisabled && "DatasheetsResultDatasheet--force-disabled")} id={printableDatasheetHolderId}>
      <div ref={forceDisabledContentRef} className="DatasheetsResultDatasheet__content">
      {datasheetElements.map((element) => {
        if (element.type === DatasheetElementType.text) {
          return (
            <TextElement
              key={element.id}
              datasheetElement={element as unknown as DatasheetElement<DatasheetTextComponentGetDto>}
            />
          );
        }

        const fieldBasedElement = element as DatasheetElement<DatasheetFieldComponentGetDto>;
        const originField = (fieldBasedElement.original as any)?.field;
        const fieldType =
          originField?.fieldtype?.typeid ?? originField?.type ?? originField?.rawFieldType;

        if (fieldType === undefined) {
          return null;
        }

        if (fieldType === FieldType.checkbox) {
          return (
            <CheckboxField 
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.table) {
          return (
            <TableField 
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
              context={context}
              globalId={globalId}
              parentFormatId={parentFormatId}
              parentTabId={parentTabId}
              formatOnly={formatOnly}
            />
          );
        }

        if (fieldType === FieldType.lookupTable) {
          return (
            <LookupTableField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.hyperlink) {
          return (
            <HyperlinkField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
              context={context}
              globalId={globalId}
            />
          );
        }

        if (fieldType === FieldType.image) {
          return (
            <ImageField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
              context={context}
              globalId={globalId}
            />
          );
        }

        if (fieldType === FieldType.dropdown) {
          return (
            <DropdownField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (
          fieldType === FieldType.number || 
          fieldType === FieldType.integer
        ) {
          return (
            <NumberField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (fieldType === FieldType.date) {
          return (
            <DateField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        if (element.type === DatasheetElementType.field) {
          return (
            <TextField
              key={fieldBasedElement.id}
              datasheetElement={fieldBasedElement}
              onChange={onDatasheetElementUpdate}
            />
          );
        }

        return null;
      })}
      </div>
      {forceDisabled && (
        <div
          className="DatasheetsResultDatasheet__shield"
          aria-hidden="true"
        />
      )}
    </div>
    </DatasheetNavigationContext.Provider>
    </FieldValidationContext.Provider>
    </DatasheetForceDisabledContext.Provider>
  );
};